import { useParams, useNavigate } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import { 
  Card, 
  Descriptions, 
  Image, 
  Space, 
  Typography, 
  Button, 
  Spin, 
  Row, 
  Col, 
  Tag,
  Divider,
  Avatar,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tooltip
} from "antd";
import { 
  ArrowLeftOutlined, 
  UserOutlined, 
  IdcardOutlined, 
  CarOutlined, 
  SafetyOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  ManOutlined,
  WomanOutlined,
  EditOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import RobinPage from "../components/RobinPage";

const { Title, Text } = Typography;

const GET_DRIVER_BY_ID = gql`
  query GetDriverById($userId: Int!) {
    getDriverById(userId: $userId) {
      email
      id
      name
      phoneNumber
      driverInfo {
        email
        id
        idNumber
        idType
        idTypeInfo {
          id
          nameAr
          nameEn
        }
        isVehicleOwner
        registrationStatus
        rejectionReason
        deliveryArea {
          nameAr
          nameEn
        }
        nationality {
          name
          nameAr
        }
        region {
          nameAr
          nameEn
        }
        vehicleType {
          nameAr
          nameEn
        }
      }
      driverIdentity {
        birthDate
        firstNameAr
        firstNameEn
        gender
        id
        lastNameAr
        lastNameEn
        image {
          url
          fileName
        }
        status
        rejectionReason
      }
      driverLicense {
        expirationDate
        id
        licenseNumber
        updatedAt
        image {
          originalName
          url
        }
        status
        rejectionReason
      }
      driverProfilePicture {
        image {
          originalName
          url
        }
        status
        rejectionReason
      }
      driverVehicleRegistration {
        ownerId
        plateNumber
        serialNumber
        vehicleBrand
        visaUserId
        image {
          originalName
          url
        }
        status
        rejectionReason
      }
    }
  }
`;

const UPDATE_DRIVER_DOCUMENT_STATUS = gql`
  mutation UpdateDriverDocumentStatus($input: UpdateDriverDocumentStatusDto!, $userId: Int!) {
    updateDriverDocumentStatus(input: $input, userId: $userId) {
      ... on DriverIdentity {
        birthDate
        createdAt
        firstNameAr
        firstNameEn
        gender
        id
        lastNameAr
        lastNameEn
        rejectionReason
        status
        updatedAt
      }
      ... on DriverLicense {
        createdAt
        expirationDate
        id
        licenseNumber
        rejectionReason
        status
        updatedAt
      }
      ... on DriverProfilePicture {
        createdAt
        id
        rejectionReason
        status
        updatedAt
      }
      ... on DriverVehicleRegistration {
        createdAt
        id
        ownerId
        plateNumber
        rejectionReason
        serialNumber
        status
        updatedAt
        vehicleBrand
        visaUserId
      }
    }
  }
`;

type DriverDetailResponse = {
  getDriverById?: {
    email?: string | null;
    id?: number | null;
    name?: string | null;
    phoneNumber?: string | null;
      driverInfo?: {
        email?: string | null;
        id?: number | null;
        idNumber?: string | null;
        idType?: string | null;
        idTypeInfo?: {
          id?: string | null;
          nameAr?: string | null;
          nameEn?: string | null;
        } | null;
        isVehicleOwner?: boolean | null;
        registrationStatus?: string | null;
        rejectionReason?: string | null;
        deliveryArea?: {
          nameAr?: string | null;
          nameEn?: string | null;
        } | null;
        nationality?: {
          name?: string | null;
          nameAr?: string | null;
        } | null;
        region?: {
          nameAr?: string | null;
          nameEn?: string | null;
        } | null;
        vehicleType?: {
          nameAr?: string | null;
          nameEn?: string | null;
        } | null;
      } | null;
    driverIdentity?: {
      birthDate?: string | null;
      firstNameAr?: string | null;
      firstNameEn?: string | null;
      gender?: string | null;
      id?: number | null;
      lastNameAr?: string | null;
      lastNameEn?: string | null;
      image?: {
        url?: string | null;
        fileName?: string | null;
      } | null;
      status?: string | null;
      rejectionReason?: string | null;
    } | null;
    driverLicense?: {
      expirationDate?: string | null;
      id?: number | null;
      licenseNumber?: string | null;
      updatedAt?: string | null;
      image?: {
        originalName?: string | null;
        url?: string | null;
      } | null;
      status?: string | null;
      rejectionReason?: string | null;
    } | null;
    driverProfilePicture?: {
      image?: {
        originalName?: string | null;
        url?: string | null;
      } | null;
      status?: string | null;
      rejectionReason?: string | null;
    } | null;
    driverVehicleRegistration?: {
      ownerId?: number | null;
      plateNumber?: string | null;
      serialNumber?: string | null;
      vehicleBrand?: string | null;
      visaUserId?: number | null;
      image?: {
        originalName?: string | null;
        url?: string | null;
      } | null;
      status?: string | null;
      rejectionReason?: string | null;
    } | null;
  } | null;
};

function DriverDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<DriverDetailResponse>(
    GET_DRIVER_BY_ID,
    {
      variables: { userId: parseInt(id || "0") },
      skip: !id,
    }
  );

  const [updateDriverDocumentStatus, { loading: updateLoading }] = useMutation(
    UPDATE_DRIVER_DOCUMENT_STATUS,
    {
      onCompleted: () => {
        message.success(t("message.success.data_updated"));
        setIsModalVisible(false);
        form.resetFields();
        setSelectedDocumentType(null);
        refetch();
      },
      onError: (error) => {
        message.error(error.message || t("message.error.data_update_failed"));
      },
    }
  );

  const driver = data?.getDriverById;

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "success";
      case "PENDING":
        return "warning";
      case "UNDER_REVIEW":
        return "processing";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const handleOpenModal = (documentType: string) => {
    setSelectedDocumentType(documentType);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedDocumentType(null);
    form.resetFields();
  };

  const getModalTitle = (documentType: string | null) => {
    if (!documentType) return t("label.driver.change_status");
    
    const titleMap: { [key: string]: string } = {
      "IDENTITY": t("label.driver.change_identity_status"),
      "DRIVERS_LICENSE": t("label.driver.change_license_status"),
      "VEHICLE_REGISTRATION": t("label.driver.change_vehicle_registration_status"),
      "PROFILE_PICTURE": t("label.driver.change_profile_picture_status"),
    };
    
    return titleMap[documentType] || t("label.driver.change_status");
  };

  const handleSubmitDocumentStatus = async (values: any) => {
    if (!id || !selectedDocumentType) return;

    // Map document type to GraphQL enum value (uppercase)
    const documentTypeMap: { [key: string]: string } = {
      "IDENTITY": "IDENTITY",
      "DRIVERS_LICENSE": "DRIVERS_LICENSE",
      "VEHICLE_REGISTRATION": "VEHICLE_REGISTRATION",
      "PROFILE_PICTURE": "PROFILE_PICTURE",
    };

    await updateDriverDocumentStatus({
      variables: {
        input: {
          documentType: documentTypeMap[selectedDocumentType] || selectedDocumentType,
          status: values.status,
          rejectionReason: values.rejectionReason || undefined,
        },
        userId: parseInt(id),
      },
    });
  };

  const renderSectionCard = (
    title: string,
    icon: React.ReactNode,
    content: React.ReactNode,
    extra?: React.ReactNode
  ) => (
    <Card
      style={{ 
        height: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: "8px"
      }}
      title={
        <Space>
          {icon}
          <Text strong style={{ fontSize: "16px" }}>{title}</Text>
        </Space>
      }
      extra={extra}
    >
      {content}
    </Card>
  );

  if (loading) {
    return (
      <RobinPage title={t("label.menu.drivers")}>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </RobinPage>
    );
  }

  if (error || !driver) {
    return (
      <RobinPage title={t("label.menu.drivers")}>
        <Card>
          <Typography.Text type="danger">
            {t("error.failed_to_load")}
          </Typography.Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate("/drivers")}>
              {t("button.back")}
            </Button>
          </div>
        </Card>
      </RobinPage>
    );
  }

  return (
    <RobinPage
      title={t("label.driver.details")}
      actions={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/drivers")}
        >
          {t("button.back")}
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Header Card with Profile */}
        <Card
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}
        >
          <Row gutter={24} align="middle">
            <Col>
              <Avatar
                size={100}
                src={driver.driverProfilePicture?.image?.url}
                icon={<UserOutlined />}
                style={{ border: "4px solid white" }}
              />
            </Col>
            <Col flex={1}>
              <Space direction="vertical" size="small">
                <Title level={2} style={{ color: "white", margin: 0 }}>
                  {driver.name || driver.driverIdentity?.firstNameEn + " " + driver.driverIdentity?.lastNameEn || "-"}
                </Title>
                <Space size="large">
                  <Space>
                    <MailOutlined style={{ color: "white" }} />
                    <Text style={{ color: "white" }}>
                      {driver.email || driver.driverInfo?.email || "-"}
                    </Text>
                  </Space>
                  <Space>
                    <PhoneOutlined style={{ color: "white" }} />
                    <Text style={{ color: "white" }}>{driver.phoneNumber || "-"}</Text>
                  </Space>
                </Space>
                {driver.driverInfo?.registrationStatus && (
                  <Tag 
                    color={getStatusColor(driver.driverInfo.registrationStatus)}
                    style={{ fontSize: "14px", padding: "4px 12px" }}
                  >
                    {t(`label.registration_status.${driver.driverInfo.registrationStatus.toLowerCase()}`, { defaultValue: driver.driverInfo.registrationStatus })}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Driver Info Section */}
        {driver.driverInfo && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderSectionCard(
                t("label.driver.info"),
                <IdcardOutlined style={{ color: "#1890ff" }} />,
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label={t("label.email")}>
                    {driver.driverInfo.email || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.id_number")}>
                    {driver.driverInfo.idNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.id_type")}>
                    {(() => {
                      const idTypeLabel = isArabic
                        ? driver.driverInfo.idTypeInfo?.nameAr
                        : driver.driverInfo.idTypeInfo?.nameEn;

                      if (idTypeLabel) {
                        return idTypeLabel;
                      }

                      if (driver.driverInfo.idType) {
                        return t(
                          `label.id_type.${driver.driverInfo.idType.toLowerCase()}`,
                          { defaultValue: driver.driverInfo.idType }
                        );
                      }

                      return "-";
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.is_vehicle_owner")}>
                    {driver.driverInfo.isVehicleOwner ? 
                      <Tag color="success">{t("label.yes")}</Tag> : 
                      <Tag color="default">{t("label.no")}</Tag>
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.registration_status")}>
                    <Tag color={getStatusColor(driver.driverInfo.registrationStatus)}>
                      {driver.driverInfo.registrationStatus 
                        ? t(`label.registration_status.${driver.driverInfo.registrationStatus.toLowerCase()}`, { defaultValue: driver.driverInfo.registrationStatus })
                        : "-"
                      }
                    </Tag>
                  </Descriptions.Item>
                  {driver.driverInfo.registrationStatus?.toUpperCase() === "REJECTED" && driver.driverInfo.rejectionReason && (
                    <Descriptions.Item label={t("label.driver.rejection_reason")}>
                      <Text type="danger">{driver.driverInfo.rejectionReason}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}
            </Col>

            <Col xs={24} lg={12}>
              {renderSectionCard(
                t("label.driver.location_info"),
                <EnvironmentOutlined style={{ color: "#52c41a" }} />,
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item
                    label={t("label.driver.region", {
                      defaultValue: isArabic ? "منطقة" : "Region",
                    })}
                  >
                    {isArabic 
                      ? (driver.driverInfo.region?.nameAr || "-")
                      : (driver.driverInfo.region?.nameEn || "-")
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.delivery_area")}>
                    {isArabic 
                      ? (driver.driverInfo.deliveryArea?.nameAr || "-")
                      : (driver.driverInfo.deliveryArea?.nameEn || "-")
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.nationality")}>
                    {isArabic 
                      ? (driver.driverInfo.nationality?.nameAr || "-")
                      : (driver.driverInfo.nationality?.name || "-")
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.vehicle_type")}>
                    {isArabic 
                      ? (driver.driverInfo.vehicleType?.nameAr || "-")
                      : (driver.driverInfo.vehicleType?.nameEn || "-")
                    }
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Col>
          </Row>
        )}

        {/* Driver Identity Section */}
        {driver.driverIdentity && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {renderSectionCard(
                t("label.driver.identity"),
                <UserOutlined style={{ color: "#722ed1" }} />,
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("label.driver.first_name_en")} span={1}>
                    {driver.driverIdentity.firstNameEn || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.last_name_en")} span={1}>
                    {driver.driverIdentity.lastNameEn || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.first_name_ar")} span={1}>
                    {driver.driverIdentity.firstNameAr || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.last_name_ar")} span={1}>
                    {driver.driverIdentity.lastNameAr || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.birth_date")} span={1}>
                    <Space>
                      <CalendarOutlined />
                      {formatDate(driver.driverIdentity.birthDate)}
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.gender")} span={1}>
                    <Space>
                      {driver.driverIdentity.gender === "MALE" ? (
                        <ManOutlined style={{ color: "#1890ff" }} />
                      ) : driver.driverIdentity.gender === "FEMALE" ? (
                        <WomanOutlined style={{ color: "#eb2f96" }} />
                      ) : null}
                      {driver.driverIdentity.gender 
                        ? t(`label.gender.${driver.driverIdentity.gender.toLowerCase()}`, { defaultValue: driver.driverIdentity.gender })
                        : "-"
                      }
                    </Space>
                  </Descriptions.Item>
                  {driver.driverIdentity.status && (
                    <Descriptions.Item label={t("label.status")} span={2}>
                      <Tag color={getStatusColor(driver.driverIdentity.status)}>
                        {driver.driverIdentity.status}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {driver.driverIdentity.status?.toUpperCase() === "REJECTED" && driver.driverIdentity.rejectionReason && (
                    <Descriptions.Item label={t("label.driver.rejection_reason")} span={2}>
                      <Text type="danger">{driver.driverIdentity.rejectionReason}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>,
                <Tooltip title={t("button.change_status")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal("IDENTITY")}
                  />
                </Tooltip>
              )}
            </Col>
            <Col xs={24} lg={8}>
              {driver.driverIdentity.image?.url && (
                <Card
                  title={t("label.driver.identity_image")}
                  style={{ height: "100%" }}
                >
                  <Image
                    src={driver.driverIdentity.image.url}
                    alt={driver.driverIdentity.image.fileName || "Identity"}
                    style={{ width: "100%", borderRadius: "8px" }}
                    preview
                  />
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Profile Picture Section */}
        {driver.driverProfilePicture && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {renderSectionCard(
                t("label.driver.profile_picture"),
                <UserOutlined style={{ color: "#eb2f96" }} />,
                <Descriptions column={1} bordered size="small">
                  {driver.driverProfilePicture.status && (
                    <Descriptions.Item label={t("label.status")}>
                      <Tag color={getStatusColor(driver.driverProfilePicture.status)}>
                        {driver.driverProfilePicture.status}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {driver.driverProfilePicture.status?.toUpperCase() === "REJECTED" && driver.driverProfilePicture.rejectionReason && (
                    <Descriptions.Item label={t("label.driver.rejection_reason")}>
                      <Text type="danger">{driver.driverProfilePicture.rejectionReason}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>,
                <Tooltip title={t("button.change_status")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal("PROFILE_PICTURE")}
                  />
                </Tooltip>
              )}
            </Col>
            <Col xs={24} lg={8}>
              {driver.driverProfilePicture.image?.url && (
                <Card
                  title={t("label.driver.profile_picture")}
                  style={{ height: "100%" }}
                >
                  <Image
                    src={driver.driverProfilePicture.image.url}
                    alt={driver.driverProfilePicture.image.originalName || "Profile Picture"}
                    style={{ width: "100%", borderRadius: "8px" }}
                    preview
                  />
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Driver License Section */}
        {driver.driverLicense && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {renderSectionCard(
                t("label.driver.license"),
                <SafetyOutlined style={{ color: "#fa8c16" }} />,
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("label.driver.license_number")}>
                    {driver.driverLicense.licenseNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.expiration_date")}>
                    {formatDate(driver.driverLicense.expirationDate)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.updated_at")} span={1}>
                    {formatDate(driver.driverLicense.updatedAt)}
                  </Descriptions.Item>
                  {driver.driverLicense.status && (
                    <Descriptions.Item label={t("label.status")} span={1}>
                      <Tag color={getStatusColor(driver.driverLicense.status)}>
                        {driver.driverLicense.status}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {driver.driverLicense.status?.toUpperCase() === "REJECTED" && driver.driverLicense.rejectionReason && (
                    <Descriptions.Item label={t("label.driver.rejection_reason")} span={2}>
                      <Text type="danger">{driver.driverLicense.rejectionReason}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>,
                <Tooltip title={t("button.change_status")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal("DRIVERS_LICENSE")}
                  />
                </Tooltip>
              )}
            </Col>
            <Col xs={24} lg={8}>
              {driver.driverLicense.image?.url && (
                <Card
                  title={t("label.driver.license_image")}
                  style={{ height: "100%" }}
                >
                  <Image
                    src={driver.driverLicense.image.url}
                    alt={driver.driverLicense.image.originalName || "License"}
                    style={{ width: "100%", borderRadius: "8px" }}
                    preview
                  />
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Vehicle Registration Section */}
        {driver.driverVehicleRegistration && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {renderSectionCard(
                t("label.driver.vehicle_registration"),
                <CarOutlined style={{ color: "#13c2c2" }} />,
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("label.driver.plate_number")}>
                    {driver.driverVehicleRegistration.plateNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.serial_number")}>
                    {driver.driverVehicleRegistration.serialNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.vehicle_brand")}>
                    {driver.driverVehicleRegistration.vehicleBrand || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("label.driver.owner_id")}>
                    {driver.driverVehicleRegistration.ownerId || "-"}
                  </Descriptions.Item>
                  {driver.driverVehicleRegistration.status && (
                    <Descriptions.Item label={t("label.status")} span={2}>
                      <Tag color={getStatusColor(driver.driverVehicleRegistration.status)}>
                        {driver.driverVehicleRegistration.status}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {driver.driverVehicleRegistration.status?.toUpperCase() === "REJECTED" && driver.driverVehicleRegistration.rejectionReason && (
                    <Descriptions.Item label={t("label.driver.rejection_reason")} span={2}>
                      <Text type="danger">{driver.driverVehicleRegistration.rejectionReason}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>,
                <Tooltip title={t("button.change_status")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal("VEHICLE_REGISTRATION")}
                  />
                </Tooltip>
              )}
            </Col>
            <Col xs={24} lg={8}>
              {driver.driverVehicleRegistration.image?.url && (
                <Card
                  title={t("label.driver.vehicle_image")}
                  style={{ height: "100%" }}
                >
                  <Image
                    src={driver.driverVehicleRegistration.image.url}
                    alt={driver.driverVehicleRegistration.image.originalName || "Vehicle"}
                    style={{ width: "100%", borderRadius: "8px" }}
                    preview
                  />
                </Card>
              )}
            </Col>
          </Row>
        )}
      </Space>

      {/* Update Document Status Modal */}
      <Modal
        title={getModalTitle(selectedDocumentType)}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitDocumentStatus}
          initialValues={{ status: undefined, rejectionReason: "" }}
        >
          <Form.Item
            name="status"
            label={t("label.status")}
            rules={[
              { required: true, message: t("message.error.field_required") },
            ]}
          >
            <Select
              placeholder={t("label.status")}
              options={[
                { value: "PENDING", label: t("label.registration_status.pending") },
                { value: "UNDER_REVIEW", label: t("label.registration_status.under_review") },
                { value: "APPROVED", label: t("label.registration_status.approved") },
                { value: "REJECTED", label: t("label.registration_status.rejected") },
              ]}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
          >
            {({ getFieldValue }) =>
              getFieldValue("status") === "REJECTED" ? (
                <Form.Item
                  name="rejectionReason"
                  label={t("label.driver.rejection_reason")}
                  rules={[
                    { required: true, message: t("message.error.field_required") },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={t("label.driver.rejection_reason")}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseModal}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                {t("button.save")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RobinPage>
  );
}

export default DriverDetail;
